export class WebSocketService {
    constructor() {
        this.client = null;
        this.handlers = {
            onOpen: () => {},
            onClose: () => {},
            onError: () => {},
            onMessage: () => {},
            onStatusChange: () => {},
        };
        this.status = 'disconnected';
    }

    async connect(connectionInfo, handlers = {}) {
        this.handlers = { ...this.handlers, ...handlers };

        try {
            // Detect if we're dealing with a Centrifugo URL
            const isCentrifugo =
                connectionInfo.token && connectionInfo.url.includes('/connection/websocket');

            if (isCentrifugo) {
                await this.connectWithCentrifugo(connectionInfo);
            } else {
                this.connectWithNativeWebSocket(connectionInfo);
            }

            return this.client;
        } catch (error) {
            console.error('WebSocket connection error:', error);
            this.handlers.onError(error);
            this.updateStatus('error');
            return null;
        }
    }

    async connectWithCentrifugo(connectionInfo) {
        try {
            // Dynamically import Centrifugo client
            const { Centrifuge } = await import('centrifuge');

            // Create Centrifuge client with token in options
            const centrifugeOptions = {
                token: connectionInfo.token,
            };
            const centrifuge = new Centrifuge(connectionInfo.url, centrifugeOptions);

            // Set up event handlers
            centrifuge.on('connecting', () => {
                this.updateStatus('connecting');
            });

            centrifuge.on('connected', () => {
                this.updateStatus('connected');
                this.handlers.onOpen();
            });

            centrifuge.on('disconnected', () => {
                this.updateStatus('closed');
                this.handlers.onClose();
            });

            centrifuge.on('error', (error) => {
                this.updateStatus('error');
                console.error('Centrifugo error:', error);
                this.handlers.onError(error);
            });

            let subscription = null;
            // Subscribe to the channel
            if (connectionInfo.channel) {
                console.log('Subscribing to channel:', connectionInfo.channel);
                subscription = centrifuge.newSubscription(connectionInfo.channel, {
                    token: connectionInfo.channel_token,
                });

                subscription.on('publication', (message) => {
                    // Create a MessageEvent-like object for compatibility
                    const event = {
                        data: JSON.stringify(message.data),
                        type: 'message',
                    };

                    this.handlers.onMessage(event);
                });

                // Add more debug events to track subscription lifecycle
                subscription.on('subscribing', () => {
                    // console.log('Attempting to subscribe to channel:', connectionInfo.channel);
                });

                subscription.on('subscribed', () => {
                    // console.log('Successfully subscribed to channel:', connectionInfo.channel);

                    // Now safely check presence after subscription is confirmed
                    subscription.presence().then(
                        function (ctx) {
                            console.log('Presence data:', ctx.clients);
                        },
                        function (err) {
                            console.log('Presence error:', err);
                        },
                    );
                });

                subscription.on('error', (err) => {
                    console.error('Subscription error for channel:', connectionInfo.channel, err);
                });

                subscription.on('unsubscribed', () => {
                    // console.log('Unsubscribed from channel:', connectionInfo.channel);
                });

                // console.log('Calling subscribe() for channel:', connectionInfo.channel);
                subscription.subscribe();
            }

            // Connect to Centrifugo
            centrifuge.connect();

            this.client = {
                centrifuge,
                close: () => {
                    if (centrifuge) {
                        centrifuge.disconnect();
                    }
                },
                getStatus: () => this.status,
            };

            return this.client;
        } catch (error) {
            console.error('Centrifugo connection error:', error);
            throw error;
        }
    }

    connectWithNativeWebSocket(connectionInfo) {
        const socket = new WebSocket(connectionInfo.url);

        socket.onopen = (event) => {
            this.updateStatus('connected');
            console.log('WebSocket opened', event);

            // Subscribe to the channel if needed (for Soketi)
            if (connectionInfo.channel && connectionInfo.auth) {
                console.log('Subscribing to channel', connectionInfo.channel);
                const subscribeMessage = {
                    event: 'pusher:subscribe',
                    data: {
                        channel: connectionInfo.channel,
                        auth: connectionInfo.auth,
                    },
                };
                socket.send(JSON.stringify(subscribeMessage));
            }

            this.handlers.onOpen(event);
        };

        socket.onclose = (event) => {
            this.updateStatus('closed');
            console.log('WebSocket closed', event);
            this.handlers.onClose(event);
        };

        socket.onerror = (event) => {
            this.updateStatus('error');
            console.log('WebSocket error', event);
            console.error('WebSocket Error:', event);
            this.handlers.onError(event);
        };

        socket.onmessage = (event) => {
            console.log('WebSocket message received', event);

            try {
                // Parse the incoming message
                const message = JSON.parse(event.data);

                // Soketi/Pusher message handling
                if (message.event && message.event.startsWith('pusher:')) {
                    console.log('Soketi system message:', message);
                    return;
                }

                // If it's an app message, extract the actual data payload
                if (message.event === 'app-message' && message.data) {
                    // Extract the actual data from the Soketi message format
                    const appData = JSON.parse(message.data);
                    console.log('Received app message:', appData);

                    // Create a new event with the proper data format
                    const newEvent = new MessageEvent('message', {
                        data: JSON.stringify(appData),
                        origin: event.origin,
                        lastEventId: event.lastEventId,
                        source: event.source,
                        ports: event.ports,
                    });

                    this.handlers.onMessage(newEvent);
                    return;
                }
            } catch (e) {
                console.error('Error processing message:', e);
            }

            // Fallback - pass the original message
            this.handlers.onMessage(event);
        };

        this.client = {
            socket,
            close: () => socket.close(),
            getStatus: () => this.status,
        };

        return this.client;
    }

    updateStatus(status) {
        this.status = status;
        this.handlers.onStatusChange(status);
    }

    close() {
        if (this.client) {
            this.client.close();
        }
    }
}
